import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, FormSubmitHandler} from 'platform/components';

import {isNotNil} from 'ramda';

import {
  useCreateCommentMutation,
  useCreateSourcingVehicleOfferQuery,
  useDeleteCommentMutation,
  useGetCommentsQuery,
  useUpdateCommentMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {CommentCardForm, CommentsCard, handleApiError, useGetUserOptions} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface CommentsTabProps extends RequiredTestIdProps {
  externalId: string;
}

export function CommentsTab(props: CommentsTabProps) {
  const {
    vehicleId,
    isLoading: isOfferLoading,
    isError: isOfferError,
  } = useCreateSourcingVehicleOfferQuery(
    {
      createSourcingVehicleOfferRequestBody: {externalId: props.externalId},
    },
    {
      selectFromResult: (result) => ({
        vehicleId: result.data?.id,
        isLoading: result.isLoading,
        isError: result.isError,
      }),
    }
  );

  const {
    data: comments,
    isLoading: isFetchingCommentsLoading,
    isError: isFetchingCommentsErrored,
  } = useGetCommentsQuery(
    isNotNil(vehicleId) ? {resourceId: EntityResourceIds.sourcing, recordId: vehicleId} : skipToken
  );
  const {userOptions} = useGetUserOptions();

  const [deleteComment, {isLoading: isDeleteCommentLoading}] = useDeleteCommentMutation();
  const [createComment, {isLoading: isSendCommentLoading}] = useCreateCommentMutation();
  const [updateComment, {isLoading: isEditCommentLoading}] = useUpdateCommentMutation();

  const onCommentSubmit: FormSubmitHandler<CommentCardForm> = async (values, _, reset) => {
    if (!vehicleId) {
      return;
    }
    await createComment({
      resourceId: EntityResourceIds.sourcing,
      recordId: vehicleId,
      createCommentRequestBody: {text: values.comment},
    })
      .unwrap()
      .then(() => {
        reset();
      })
      .catch(handleApiError);
  };

  const onCommentDelete = async (commentId: string) => {
    if (!vehicleId) {
      return;
    }
    await deleteComment({
      resourceId: EntityResourceIds.sourcing,
      recordId: vehicleId,
      commentId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const onCommentEdit = async (commentId: string, text: string) => {
    if (!vehicleId) {
      return;
    }
    await updateComment({
      resourceId: EntityResourceIds.sourcing,
      recordId: vehicleId,
      commentId,
      updateCommentRequestBody: {text},
    })
      .unwrap()
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isOfferLoading} isError={isOfferError}>
      <CommentsCard
        title={i18n.t('page.comments.labels.comments')}
        comments={comments}
        isSendCommentLoading={isSendCommentLoading}
        isFetchingCommentsLoading={isFetchingCommentsLoading}
        isFetchingCommentsErrored={isFetchingCommentsErrored}
        isDeleteCommentLoading={isDeleteCommentLoading}
        isEditCommentLoading={isEditCommentLoading}
        onSubmit={onCommentSubmit}
        onDelete={onCommentDelete}
        onEdit={onCommentEdit}
        userOptions={userOptions}
        data-testid={suffixTestId('comment', props)}
      />
    </DataStatus>
  );
}
